/**
 * Dashboard hubs — the three pages the Dashboards nav flyout and the Home
 * Dashboards tile open: vTrackers, Norm Databases and Visit Metrics Center.
 *
 * They replace the CMS-driven category pages (Current market trends, Brand
 * image) as the way into the native dashboards (M4S-5280). Each dashboard is
 * reached at `/<hub slug>/<dashboard code>`, served by the existing
 * `[slug]/[dashboard]` route, and keeps the access rules of the path it used
 * to live at (`legacyPath`) — see `legacyDashboardPath`.
 *
 * The nav flyout and the Home tile both read `DASHBOARD_HUB_LINKS`, so the two
 * surfaces cannot list different hubs.
 */

import { STUDIO_NAV_FEATURE_FLAGS } from '@/@studio/types/constants';

export interface DashboardHubCard {
    /** Native dashboard code (`src/lib/dashboards/registry`). */
    code: string;
    title: string;
    body: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    /** Only shown when this feature flag is on. */
    requiresFlag?: string;
    /** Where this dashboard lived before the hubs. Its CMS card permission
     * and home-card checks are matched against this path. */
    legacyPath: string;
}

export interface DashboardHub {
    slug: string;
    /** Nav flyout / Home tile label. */
    name: string;
    title: string;
    subtitle: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    /** Shows Studio's filter drawer on the dashboards opened from this hub —
     * the same choice their legacy category made. */
    showFilters: boolean;
    cards: DashboardHubCard[];
}

export const VTRACKERS_HUB_SLUG = 'vtrackers';

export const DASHBOARD_HUBS: DashboardHub[] = [
    {
        slug: VTRACKERS_HUB_SLUG,
        name: 'vTrackers',
        title: 'vTrackers',
        subtitle: 'Brand trackers, wave over wave.',
        icon: 'radar',
        // Internal only: `/vtrackers` is in INTERNAL_ROUTES (routeAccess.ts).
        showFilters: false,
        cards: [
            {
                code: 'burlington_vtracker',
                title: 'Burlington vTracker+',
                body: 'Brand perception, store presentation and NPS trends for Burlington.',
                icon: 'store',
                legacyPath: '/custom-dashboards/burlington_vtracker',
            },
            {
                code: 'cargurus_app_and_web_visitors_vtracker',
                title: 'Cargurus vTracker+',
                body: 'Scorecard and question detail for the CarGurus tracker.',
                icon: 'car',
                legacyPath: '/custom-dashboards/cargurus_app_and_web_visitors_vtracker',
            },
            {
                code: 'nacs_vtracker_plus',
                title: 'NACs VTracker+',
                body: 'Single and multiple selection question results for the NACS tracker.',
                icon: 'fuel',
                legacyPath: '/custom-dashboards/nacs_vtracker_plus',
            },
        ],
    },
    {
        slug: 'norm-databases',
        name: 'Norm Databases',
        title: 'Norm Databases',
        subtitle: 'Norms and benchmarks for ABC/DTS and iOOH studies.',
        icon: 'database',
        showFilters: false,
        cards: [
            {
                code: 'abc_norms',
                title: 'ABC/DTS Norms',
                body: 'Promo, VF, key show and key art norms for ABC and DTS studies.',
                icon: 'bar-chart-3',
                // Same gate as the legacy Custom Dashboard card.
                requiresFlag: STUDIO_NAV_FEATURE_FLAGS.ABC_DTS_NORMS,
                legacyPath: '/custom-dashboards/abc_norms',
            },
            {
                code: 'iooh_brand_lift',
                title: 'iOOH Brand Lift',
                body: 'Brand lift results for iOOH campaigns.',
                icon: 'monitor-play',
                legacyPath: '/custom-dashboards/iooh_brand_lift',
            },
        ],
    },
    {
        slug: 'visit-metrics-center',
        name: 'Visit Metrics Center',
        title: 'Visit Metrics Center',
        subtitle: 'Explore app, web and location visitation behavior.',
        icon: 'map-pin',
        showFilters: true,
        cards: [
            {
                code: 'app',
                title: 'App Events Explorer',
                body: 'View, analyze & enrich app usage data.',
                icon: 'smartphone',
                legacyPath: '/current-market-trends/app',
            },
            {
                code: 'website',
                title: 'Web Events Explorer',
                body: 'View, analyze & enrich website visit data.',
                icon: 'globe',
                legacyPath: '/current-market-trends/website',
            },
            {
                code: 'location',
                title: 'Location Events Explorer',
                body: 'View, analyze & enrich venue visit data.',
                icon: 'map-pin',
                legacyPath: '/current-market-trends/location',
            },
            {
                code: 'visitor_trends',
                title: 'Visitor Trends',
                body: 'Visitation reach over time for venues, apps and websites.',
                icon: 'trending-up',
                legacyPath: '/brand-image/visitor_trends',
            },
        ],
    },
];

export function getDashboardHub(slug: string | undefined): DashboardHub | undefined {
    return DASHBOARD_HUBS.find((h) => h.slug === slug);
}

export function hubDashboardHref(hub: DashboardHub, code: string): string {
    return `/${hub.slug}/${code}`;
}

/** The hubs as nav flyout rows / Home tile actions. */
export const DASHBOARD_HUB_LINKS: { name: string; icon: string; href: string }[] =
    DASHBOARD_HUBS.map((h) => ({ name: h.name, icon: h.icon, href: `/${h.slug}` }));

/**
 * The legacy path whose permission checks apply to a hub dashboard path, or
 * null for any other path. `/norm-databases/abc_norms` → `/custom-dashboards/abc_norms`.
 */
export function legacyDashboardPath(pathname: string | null | undefined): string | null {
    if (!pathname) return null;
    const [, slug, code, ...rest] = pathname.replace(/\/$/, '').split('/');
    if (!code || rest.length > 0) return null;
    const card = getDashboardHub(slug)?.cards.find((c) => c.code === code);
    return card?.legacyPath ?? null;
}
